// ============================================================================
// WARDROBE
// ============================================================================

import CONFIG from '../config.js';
import { lerp, easeOutElastic } from '../utils/helpers.js';
import { DetachedDoor } from './DetachedDoor.js';
import { DetachedMirrorPiece } from './DetachedMirrorPiece.js';
import { BreakableEntity } from './BreakableEntity.js';

export class Wardrobe extends BreakableEntity {
    constructor(x, y, physics = null) {
        super(x, y, 70, 80, physics);
        this.doorWidth = 30;  // Single sliding door
        this.doorHeight = 70;  // Door height
        
        this.isOpen = false;
        this.isAnimating = false;
        this.doorSlide = 0;  // Sliding position (0 to doorWidth)
        this.targetSlide = 0;
        this.animationTime = 0;
        this.animationDuration = 0;
        this.wobbleTime = 0;
        this.wobbleAmplitude = 0;
        this.tilt = 0;
        this.overshoot = 0;
        
        // Create Matter.js body (static when not broken)
        if (physics) {
            this.body = physics.createRectangle(x, y, this.width, this.height, {
                isStatic: true,
                frictionAir: 0,
                friction: 0,
                restitution: 0
            });
            physics.linkEntity(this, this.body);
        }
    }

    canInteract() {
        return !this.isAnimating;
    }

    performAction(level, aggression, game) {
        this.isOpen = !this.isOpen;
        this.targetSlide = this.isOpen ? this.doorWidth : 0;

        this.isAnimating = true;
        this.animationTime = 0;
        this.animationDuration = CONFIG.doorOpenSpeed[level];
        this.overshoot = CONFIG.doorOvershoot[level];

        if (level >= 1) {
            this.wobbleAmplitude = level * 2;
            this.wobbleTime = 0.5;
        }

        // Tilt increases with aggression (aggression already starts at 0 after interactionBaselineTime)
        if (aggression > 0) {
            this.tilt += aggression * 0.5;
            this.tilt = Math.min(this.tilt, CONFIG.maxTilt);
        }
    }

    getBreakThreshold() {
        return CONFIG.wardrobeBreakThreshold;
    }

    onBreak(game) {
        let wardrobeColor = CONFIG.colors.cupboard;
        if (this.damage >= CONFIG.wardrobeDamageThresholds[2]) {
            wardrobeColor = '#4B2010';
        } else if (this.damage >= CONFIG.wardrobeDamageThresholds[1]) {
            wardrobeColor = '#6B3410';
        } else if (this.damage >= CONFIG.wardrobeDamageThresholds[0]) {
            wardrobeColor = '#8B4010';
        }

        // Cap force amount for spawn velocity (prevents tunneling), but damage was already applied uncapped
        const cappedForce = Math.min(this.lastDamageAmount, CONFIG.throwableMaxAggression);
        
        // Create detached door (sliding door breaks off)
        const doorX = this.x - this.width / 2 + this.doorSlide;
        game.detachedDoors.push(new DetachedDoor(
            doorX,
            this.y,
            this.doorWidth,
            this.doorHeight,
            wardrobeColor,
            true,  // isLeftDoor
            cappedForce,
            this.physics
        ));

        // Create 3 mirror pieces
        const mirrorY = this.y - this.height / 2 + 20;
        const mirrorSize = 15;
        for (let i = 0; i < 3; i++) {
            const mirrorX = this.x + (i - 1) * 10;
            if (!game.detachedMirrorPieces) {
                game.detachedMirrorPieces = [];
            }
            game.detachedMirrorPieces.push(new DetachedMirrorPiece(
                mirrorX,
                mirrorY,
                mirrorSize,
                i,
                this.physics,
                cappedForce
            ));
        }
    }

    update(dt) {
        // Sync position from physics body (if not broken)
        if (this.body && !this.isBroken) {
            this.x = this.body.position.x;
            this.y = this.body.position.y;
        }
        
        if (this.isAnimating) {
            this.animationTime += dt;
            const progress = Math.min(this.animationTime / this.animationDuration, 1);

            const easedProgress = easeOutElastic(progress);
            const overshootSlide = this.targetSlide + (this.isOpen ? this.overshoot : -this.overshoot);

            if (this.overshoot > 0) {
                if (progress < 0.7) {
                    this.doorSlide = lerp(this.isOpen ? 0 : this.doorWidth, overshootSlide, progress / 0.7);
                } else {
                    this.doorSlide = lerp(overshootSlide, this.targetSlide, (progress - 0.7) / 0.3);
                }
            } else {
                this.doorSlide = lerp(this.isOpen ? 0 : this.doorWidth, this.targetSlide, easedProgress);
            }

            if (progress >= 1) {
                this.doorSlide = this.targetSlide;
                this.isAnimating = false;
            }
        }

        if (this.wobbleTime > 0) {
            this.wobbleTime -= dt;
        }
    }

    draw(ctx, camera) {
        ctx.save();

        let wobbleX = 0;
        let wobbleY = 0;
        if (this.wobbleTime > 0) {
            wobbleX = Math.sin(this.wobbleTime * 30) * this.wobbleAmplitude * (this.wobbleTime / 0.5);
            wobbleY = Math.cos(this.wobbleTime * 30) * this.wobbleAmplitude * (this.wobbleTime / 0.5);
        }

        const drawX = this.x + wobbleX;
        const drawY = this.y + wobbleY;

        ctx.translate(drawX, drawY);
        ctx.rotate((this.tilt * Math.PI) / 180);
        ctx.translate(-drawX, -drawY);

        // --- 3/4 PERSPECTIVE SETTINGS ---
        const depth = 20; 
        const heightOffset = 60; // Taller than cupboard
        
        let wardrobeColor = CONFIG.colors.cupboard;
        if (this.damage >= CONFIG.wardrobeDamageThresholds[2]) wardrobeColor = '#4B2010';
        else if (this.damage >= CONFIG.wardrobeDamageThresholds[1]) wardrobeColor = '#6B3410';
        else if (this.damage >= CONFIG.wardrobeDamageThresholds[0]) wardrobeColor = '#8B4010';

        const darkerColor = '#3d1c07';
        const topColor = '#a65d2e';

        // 1. Draw SIDE/THICKNESS
        ctx.fillStyle = darkerColor;
        ctx.fillRect(drawX - this.width / 2, drawY - this.height / 2 - heightOffset, this.width, this.height + heightOffset);

        // 2. Draw TOP SURFACE
        ctx.fillStyle = topColor;
        ctx.fillRect(drawX - this.width / 2, drawY - this.height / 2 - heightOffset, this.width, depth);
        ctx.strokeStyle = '#000';
        ctx.strokeRect(drawX - this.width / 2, drawY - this.height / 2 - heightOffset, this.width, depth);

        // 3. Draw FRONT FACE
        const frontY = drawY - this.height / 2 - heightOffset + depth;
        const frontHeight = this.height + heightOffset - depth;

        ctx.fillStyle = wardrobeColor;
        ctx.fillRect(drawX - this.width / 2, frontY, this.width, frontHeight);
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 2;
        ctx.strokeRect(drawX - this.width / 2, frontY, this.width, frontHeight);

        if (!this.isBroken) {
            // 1. Background panel (always visible, stays in place)
            // This is already drawn as the front face above, so we don't need to redraw it
            
            // 2. Mirror (always visible when not broken, drawn before door)
            const mirrorWidth = this.doorWidth;
            const mirrorHeight = frontHeight * 0.7; // Slightly shorter than door
            const mirrorX = drawX + this.width / 2 + 5 - (1.5 * mirrorWidth); // Positioned to the right of wardrobe, then moved left by 1.5x width
            const mirrorY = frontY + (frontHeight - mirrorHeight) / 2; // Centered vertically
            
            // Mirror frame
            ctx.fillStyle = '#8B4513'; // Brown frame
            ctx.fillRect(mirrorX, mirrorY - 2, mirrorWidth + 4, mirrorHeight + 4);
            ctx.strokeStyle = '#000';
            ctx.lineWidth = 2;
            ctx.strokeRect(mirrorX, mirrorY - 2, mirrorWidth + 4, mirrorHeight + 4);
            
            // Mirror glass
            ctx.fillStyle = '#C0C0C0'; // Silver mirror surface
            ctx.fillRect(mirrorX + 2, mirrorY, mirrorWidth, mirrorHeight);
            ctx.fillStyle = 'rgba(200, 200, 255, 0.3)'; // Slight blue tint
            ctx.fillRect(mirrorX + 2, mirrorY, mirrorWidth, mirrorHeight);

            // 3. Interior (only visible where door was, when door is open)
            // Only draw interior in the area that was covered by the door (left side, width = doorWidth)
            if (this.doorSlide > 0) {
                const interiorColor = '#8B6914';
                ctx.fillStyle = interiorColor;
                // Only draw interior where the door was (left side, doorWidth wide)
                ctx.fillRect(drawX - this.width / 2 + 2, frontY + 2, this.doorWidth - 2, frontHeight - 4);
            }

            // 4. Sliding door (drawn on top of everything)
            ctx.save();
            const doorX = drawX - this.width / 2 + this.doorSlide;
            ctx.translate(doorX, frontY);
            ctx.fillStyle = wardrobeColor;
            ctx.fillRect(0, 0, this.doorWidth, frontHeight);
            ctx.strokeStyle = '#000';
            ctx.lineWidth = 1;
            ctx.strokeRect(0, 0, this.doorWidth, frontHeight);

            // Handle
            ctx.fillStyle = '#FFD700';
            ctx.beginPath(); ctx.arc(this.doorWidth - 5, frontHeight / 2, 2, 0, Math.PI * 2); ctx.fill();
            ctx.restore();
        } else {
            // Broken interior
            ctx.fillStyle = '#8B6914';
            ctx.fillRect(drawX - this.width / 2 + 2, frontY + 2, this.width - 4, frontHeight - 4);
        }

        // Cracks
        if (this.damage >= CONFIG.wardrobeDamageThresholds[0]) {
            ctx.strokeStyle = '#000';
            ctx.lineWidth = 1;
            ctx.globalAlpha = 0.5;
            const numCracks = Math.min(Math.floor(this.damage / 5), 8);
            for (let i = 0; i < numCracks; i++) {
                const x1 = drawX + (Math.random() - 0.5) * this.width;
                const y1 = frontY + (Math.random() - 0.5) * frontHeight;
                ctx.beginPath(); ctx.moveTo(x1, y1);
                ctx.lineTo(x1 + (Math.random() - 0.5) * 20, y1 + (Math.random() - 0.5) * 20);
                ctx.stroke();
            }
            ctx.globalAlpha = 1;
        }

        ctx.restore();
    }
}
